// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, useCallback } from 'react';

import {
  type Task,
  type WorkOrder,
  useEvolveGet,
  type EvolveApiBody,
  useEvolvePatch,
  type TaskPaginated,
} from '@evolvemep/api-ts';
import { Stack, Text, Flex, Card, type TextProps, Loader, Space } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { AttachmentsPopover } from 'components/Mantine/Attachments/AttachmentsPopover';
import { CustomPropertiesPopover } from 'components/Mantine/CustomProperties/CustomPropertiesPopover';
import { EntityStatusBadge } from 'components/Mantine/EntityStatusBadge';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { UserInfoList } from 'components/Mantine/UserInfoBadge';
import { ActionTaskMenu } from 'modules/Shop/Tasks/ActionTaskMenu';
import { getDisplayNameWO, isReleased, hasEverBeenReleased } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';
import { EditTaskMenu } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/EditTaskMenu';
import { PredecessorsBadge } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/PredecessorsBadge';
import { useCanEditWorkOrder } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';

import { TaskTimeBadge } from '../TaskTimeBadge';

type Props = {
  task: Task | TaskPaginated;
  onTaskUpdated: (updatedTask: Task) => void;
  workOrder: WorkOrder;
  onDeleteTask: () => void;
  hideDetails?: boolean;
};

export const WorkCellName = ({
  task,
  c,
  ...textProps
}: { task: Pick<Task, 'workCellName' | 'hasMovedToCustomWorkCell'> } & TextProps) => {
  const workCellIsUnassigned = task.workCellName === 'Unassigned';
  return (
    <Flex align="center" gap={6}>
      <AgGridStyleTooltip label="Work cell" withArrow hidden={workCellIsUnassigned}>
        <Text {...textProps} c={workCellIsUnassigned ? 'orange.7' : c}>
          {workCellIsUnassigned && 'Work Cell '}
          {task.workCellName ?? '--'}
        </Text>
      </AgGridStyleTooltip>
      {task.hasMovedToCustomWorkCell && (
        <AgGridStyleTooltip
          label="The work cell for this task was set manually."
          withArrow
          openDelay={0}
          position="right"
        >
          <EvolveIcon icon="Override" color="dimmed" size="sm" />
        </AgGridStyleTooltip>
      )}
    </Flex>
  );
};

const EditableTaskMenu = ({
  task,
  onTaskUpdated,
  workOrder,
  onDeleteTask,
}: Pick<Props, 'task' | 'onTaskUpdated' | 'workOrder' | 'onDeleteTask'>) => {
  const { canEditWorkOrder } = useCanEditWorkOrder(workOrder);
  const editable = canEditWorkOrder('editTasks');
  const actionable = canEditWorkOrder('actionTasks');
  if (editable) {
    return <EditTaskMenu workOrder={workOrder} task={task} onTaskUpdated={onTaskUpdated} onDeleteTask={onDeleteTask} />;
  }
  if (actionable) {
    return (
      <ActionTaskMenu
        task={task}
        onTaskUpdated={onTaskUpdated}
        parentPage={workOrder.identifier}
        facility={{
          facilityId: workOrder.facilityId,
          facilityName: workOrder.facilityName,
        }}
      />
    );
  }
  return null;
};

export const EditableTask = ({
  task: initialTask,
  onTaskUpdated: onTaskUpdatedParent,
  workOrder,
  onDeleteTask,
  hideDetails,
}: Props) => {
  const navigate = useEvolveNavigate(getDisplayNameWO(workOrder));
  const [task, setTask] = useState<Task | TaskPaginated>(initialTask);

  const canNavToTaskViewer = !hideDetails && (isReleased(workOrder) || task.taskStatusTypeName === 'Completed');

  const onTaskUpdated = useCallback(
    (updatedTask: Task) => {
      onTaskUpdatedParent(updatedTask);
      setTask(updatedTask);
    },
    [onTaskUpdatedParent],
  );

  const { apiCall: refreshTaskApiCall, loading } = useEvolveGet(`shop/task/${task.taskId}`);
  const refreshTask = useCallback(
    async () => refreshTaskApiCall().then(onTaskUpdated),
    [onTaskUpdated, refreshTaskApiCall],
  );

  const { apiCall: patchTask, loading: saving } = useEvolvePatch(`shop/task/${task.taskId}`);
  const updateTask = useCallback(
    async (body: EvolveApiBody<'PATCH', 'shop/task/:taskId'>) => patchTask(body).then(onTaskUpdated),
    [patchTask, onTaskUpdated],
  );

  return (
    <Card
      py="xs"
      className={canNavToTaskViewer ? 'editable-work-order-item' : undefined}
      onClick={() => canNavToTaskViewer && navigate(`/task-viewer/${task.taskId}`)}
      bg="gray.0"
    >
      <Flex align="center" justify="space-between">
        <Stack gap={0} flex={1}>
          <Text>{task.taskName}</Text>
          <WorkCellName task={task} c="dimmed" fz="sm" />
          <Space my={1} />
          <UserInfoList
            users={task.assignees.map(({ user }) => user)}
            fallback={
              <Text fz="sm" c="orange.7">
                No assignees
              </Text>
            }
          />
        </Stack>
        <Flex gap="sm" align="center" flex="0 0 auto">
          {!hideDetails && hasEverBeenReleased(workOrder) && (
            <EntityStatusBadge customStatusEntityName="Task" stateName={task.taskStatusTypeName} size="xl" />
          )}

          <TaskTimeBadge
            estimatedTime={task.estimatedTimeInSeconds}
            taskTime={task.taskTimeInSeconds}
            isStarted={task.taskStatusTypeName !== 'Not Started'}
          />

          <PredecessorsBadge task={task} workOrderState={workOrder.workOrderStateName} />

          <CustomPropertiesPopover
            entity={task}
            disabled={saving}
            customPropertyTypeName="Task"
            onUpdateCustomProperty={(customPropertyId, value) =>
              updateTask({
                customProperties: [{ customPropertyId, value }],
              })
            }
          />

          <AttachmentsPopover
            taskId={task.taskId}
            onFilesUploaded={() => refreshTask()}
            onWebLinkAdded={() => refreshTask()}
            onAttachmentDeleted={() => refreshTask()}
            numberOfAttachments={task.numberOfAttachments}
            size="xl"
            allowUploads="task"
            showWhenEmpty
          />
          {loading ? (
            <Loader size="sm" />
          ) : (
            <EditableTaskMenu
              task={task}
              onTaskUpdated={onTaskUpdated}
              workOrder={workOrder}
              onDeleteTask={onDeleteTask}
            />
          )}
        </Flex>
      </Flex>
    </Card>
  );
};
